import { lazy, Suspense, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Founder } from "@/components/Founder";
import { Statistics } from "@/components/Statistics";
import { Services } from "@/components/Services";
import { Industries } from "@/components/Industries";
import { About } from "@/components/About";
import { WhyChooseUs } from "@/components/WhyChooseUs";
import { Process } from "@/components/Process";
import { LeadForm } from "@/components/LeadForm";
import { Testimonials } from "@/components/Testimonials";
import { FAQ } from "@/components/FAQ";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { ScrollProgress } from "@/components/ScrollProgress";
import { BackToTop } from "@/components/BackToTop";
import { LoadingScreen } from "@/components/LoadingScreen";

const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://vrmarketinghub.in";

const LandingSEO = () => (
  <Helmet>
    <title>VR Marketing Hub | AI-Powered Digital Marketing Agency in India</title>
    <meta name="description" content="VR Marketing Hub helps Indian businesses grow with Meta Ads, Instagram Ads, AI Content, Lead Generation, Landing Pages and Website Development. Book a FREE strategy call today." />
    <meta name="robots" content="index, follow" />
    <link rel="canonical" href={SITE_URL + "/"} />
    <meta property="og:url" content={SITE_URL + "/"} />
  </Helmet>
);

const Landing = () => {
  const [loaded, setLoaded] = useState(false);
  const { hash } = useLocation();

  // When arriving on the landing with a hash (e.g. from footer link on /privacy-policy → "/#services"),
  // scroll the target section into view once the page has settled.
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    const id = hash.slice(1);
    const scrollTo = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    // Wait for loading screen + layout to settle
    const t = setTimeout(scrollTo, 1400);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <>
      <LandingSEO />
      <LoadingScreen onFinish={() => setLoaded(true)} />
      <div className={`min-h-screen bg-[#050B14] text-white antialiased transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}>
        <ScrollProgress />
        <Navbar />
        <main>
          <Hero />
          <Founder />
          <Statistics />
          <Services />
          <Industries />
          <About />
          <WhyChooseUs />
          <Process />
          <Testimonials />
          <LeadForm />
          <FAQ />
          <Contact />
        </main>
        <Footer />
        <FloatingWhatsApp />
        <BackToTop />
      </div>
    </>
  );
};

// Scrolls to top when route changes (so /privacy-policy opens at the top, not mid-page)
const ScrollToTopOnNav = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, left: 0 });
  }, [pathname, hash]);
  return null;
};

const AdminFallback = () => (
  <div className="min-h-screen bg-[#050B14] text-white grid place-items-center">
    <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">Loading…</div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTopOnNav />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route
              path="/privacy-policy"
              element={
                <Suspense fallback={<AdminFallback />}>
                  <PrivacyPolicy />
                </Suspense>
              }
            />
            <Route
              path="/terms-and-conditions"
              element={
                <Suspense fallback={<AdminFallback />}>
                  <Terms />
                </Suspense>
              }
            />
            <Route
              path="/admin-login"
              element={
                <Suspense fallback={<AdminFallback />}>
                  <Helmet><title>Admin Login · VR Marketing Hub</title><meta name="robots" content="noindex, nofollow" /></Helmet>
                  <AdminLogin />
                </Suspense>
              }
            />
            <Route
              path="/admin"
              element={
                <Suspense fallback={<AdminFallback />}>
                  <Helmet><title>Admin · VR Marketing Hub</title><meta name="robots" content="noindex, nofollow" /></Helmet>
                  <AdminDashboard />
                </Suspense>
              }
            />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
